import { useState, useCallback } from 'react';
import { message } from 'antd';
import { pwd210Api, FD210, FD211 } from '../../../services/pwd210Api';
import { useAuth } from '../../../contexts/AuthContext';
import { useFieldErrors } from '../../../hooks/useFieldErrors';
import { extractApiErrorMessage } from '../../../utils/errorHandler';
import { Mode } from './usePwd210Mode';

export function usePwd210Record() {
  const { user } = useAuth();
  const [record, setRecord] = useState<FD210 | null>(null);
  const [formData, setFormData] = useState<Partial<FD210>>({});
  const [details, setDetails] = useState<Partial<FD211>[]>([]);
  const [selectedRow, setSelectedRow] = useState(0);
  const [loading, setLoading] = useState(false);
  const { fieldErrors, setFieldErrors, clearFieldErrors, getFieldError, hasErrors } = useFieldErrors();

  /** Load a single record by ID */
  const loadRecord = useCallback(async (id: string): Promise<{
    data: FD210;
    dets: Partial<FD211>[];
  } | null> => {
    setLoading(true);
    try {
      const data = await pwd210Api.get(id);
      setRecord(data);
      setFormData({ ...data });
      const dets = data.details || [];
      setDetails(dets);
      setSelectedRow(0);
      return { data, dets };
    } catch {
      message.error('載入失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /** Set a single master field */
  const setField = useCallback((key: keyof FD210, value: FD210[keyof FD210]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Get a single master field value */
  const getField = useCallback((key: keyof FD210): FD210[keyof FD210] => {
    return (formData as Record<string, FD210[keyof FD210]>)[key] ?? '';
  }, [formData]);

  /** Get a detail field value from the selected row */
  const getDetailField = useCallback((key: keyof FD211): FD211[keyof FD211] => {
    const sel = details[selectedRow] as Partial<FD211> | undefined;
    return sel ? (sel as Record<string, FD211[keyof FD211]>)[key] ?? '' : '';
  }, [details, selectedRow]);

  /** Set a detail field value on the selected row */
  const setDetailField = useCallback((key: keyof FD211, value: FD211[keyof FD211]) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[selectedRow]) {
        next[selectedRow] = { ...next[selectedRow], [key]: value };
      }
      return next;
    });
  }, [selectedRow]);

  /** Save (create or update) */
  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: FD210) => void | Promise<void>,
  ) => {
    setLoading(true);
    try {
      const req = {
        master: formData as FD210,
        details: details as FD211[],
      };
      if (mode === 'add') {
        const result = await pwd210Api.create(req);
        message.success(`新增成功: ${result.fd210_01}`);
        await onSuccess(result);
      } else {
        const result = await pwd210Api.update(formData.fd210_01!, req);
        message.success('修改成功');
        setRecord(result);
        setFormData({ ...result });
        setDetails(result.details || []);
        await onSuccess(result);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { errors?: { field: string; message: string }[]; error?: string } } };
      const errors = err.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        setFieldErrors(errors);
        message.error('請修正標示的欄位');
      } else {
        message.error(extractApiErrorMessage(err, '儲存失敗'));
      }
    } finally {
      setLoading(false);
    }
  }, [formData, details, setFieldErrors]);

  /** Delete the current record */
  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (!record) return false;
    try {
      await pwd210Api.delete(record.fd210_01);
      message.success('刪除成功');
      return true;
    } catch {
      message.error('刪除失敗');
      return false;
    }
  }, [record]);

  /** Add a new detail row */
  const addDetailRow = useCallback(() => {
    const maxSeq = details.reduce((max, d) => Math.max(max, d.fd211_02 || 0), 0);
    setDetails(prev => [...prev, { fd211_02: maxSeq + 1, fd211_01: formData.fd210_01 }]);
  }, [details, formData.fd210_01]);

  /** Delete the currently selected detail row and reindex (T-F05) */
  const deleteDetailRow = useCallback(() => {
    if (details.length <= 0) return;
    setDetails(prev => {
      const next = prev.filter((_, i) => i !== selectedRow);
      return next.map((d, i) => ({ ...d, fd211_02: i + 1 }));
    });
    setSelectedRow(Math.max(0, selectedRow - 1));
  }, [details.length, selectedRow]);

  /** Reset form to current record (revert) */
  const revertToRecord = useCallback(() => {
    clearFieldErrors();
    if (record) {
      setFormData({ ...record });
      setDetails(record.details || []);
    }
  }, [record, clearFieldErrors]);

  /** Reset form for a new add */
  const resetForAdd = useCallback(() => {
    clearFieldErrors();
    setRecord(null);
    setFormData({
      fd210_12: user?.userId || '',
      fd210_13: 1,
      fd210_34: false,
      fd210_35: false,
      fd210_36: false,
      fd210_37: false,
      fd210_38: false,
      fd210_39: false,
    });
    setDetails([{ fd211_02: 1 }]);
    setSelectedRow(0);
  }, [user, clearFieldErrors]);

  const startFromSource = useCallback((draft: FD210) => {
    clearFieldErrors();
    setFormData({ ...draft });
    setDetails(draft.details || []);
    setSelectedRow(0);
  }, [clearFieldErrors]);

  /** Apply result after save */
  const applyResult = useCallback((result: FD210) => {
    setRecord(result);
    setFormData({ ...result });
    setDetails(result.details || []);
  }, []);

  return {
    record, formData, details, selectedRow, loading,
    setSelectedRow, setDetails,
    loadRecord, setField, getField,
    getDetailField, setDetailField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    revertToRecord, resetForAdd, applyResult, startFromSource,
    fieldErrors, getFieldError, clearFieldErrors, hasErrors,
  };
}
